import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, BookOpen, Home } from "lucide-react";
import { openChat } from "@/lib/chat";

export default function Inquiry() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    client_name: user?.name || "",
    client_email: user?.email || "",
    business_name: "",
    goals: "",
    budget: "",
    timeline: "",
    deliverables: "",
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
      toast.success("Brief submitted. We'll send a quote within 48 hours.");
      openChat({
        context: `Brief submitted · ${form.business_name || "unknown business"}`,
        email: form.client_email,
        name: form.client_name,
      });
    } catch (err) {
      toast.error("Failed to submit brief");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="inquiry-success">
        <div className="inline-flex w-16 h-16 rounded-full bg-[#ac62ea]/12 items-center justify-center mb-7">
          <Check className="h-8 w-8 text-[#ac62ea]" />
        </div>
        <div className="label-mono text-[#ac62ea]" data-testid="inquiry-success-label">Brief received</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-[-0.035em] font-semibold">
          Thanks — we&rsquo;ve got it.
        </h1>
        <p className="text-muted-foreground mt-5 max-w-lg mx-auto leading-relaxed text-lg">
          We&apos;ll review your brief and send back questions or a directional quote — usually within 48 hours.
        </p>
        <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center" data-testid="inquiry-success-cta-row">
          <Button asChild className="rounded-full bg-[#0A0A0A] hover:bg-black/85 h-11 px-6" data-testid="inquiry-home-cta">
            <Link to="/"><Home className="h-4 w-4 mr-2" /> Back to home</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-11 px-6" data-testid="inquiry-insights-cta">
            <Link to="/articles"><BookOpen className="h-4 w-4 mr-2" /> Browse insights <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="inquiry-page">
      <div className="label-mono">Custom Project</div>
      <h1 className="font-display text-5xl font-medium mt-2 tracking-tight">Send a brief.</h1>
      <p className="text-muted-foreground mt-4 max-w-xl leading-relaxed">
        For multi-month engagements or anything off the menu. We&apos;ll review and respond with a scoped quote and payable invoice.
      </p>
      <div className="mt-5 rounded-xl border border-[#ac62ea]/25 bg-[#f3eefb] p-4 text-sm leading-relaxed" data-testid="inquiry-sow-callout">
        <strong className="block mb-1">Custom projects are governed by their own agreement.</strong>
        <span className="text-foreground/80">
          Custom-scoped work is subject to a separate Master Services Agreement and Statement of Work (SOW) — including custom pricing, milestones, IP terms and refund treatment. The{" "}
          <Link to="/terms" className="text-[#ac62ea] hover:underline">Online Services Agreement</Link>
          {" "}on this site only applies to engagements booked and paid online.
        </span>
      </div>
      <p className="text-sm text-muted-foreground mt-3" data-testid="inquiry-sales-contact">
        Prefer to talk first?{" "}
        <button type="button" onClick={() => openChat({ context: "Inquiry · Sales conversation" })} className="text-[#ac62ea] hover:underline font-medium" data-testid="inquiry-sales-chat-trigger">Chat with us</button>
        {" "}or{" "}
        <Link to="/book" className="text-[#ac62ea] hover:underline" data-testid="inquiry-book-now-link">book now</Link>
      </p>

      <form onSubmit={submit} className="mt-12 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-mono">Your name</Label>
            <Input className="rounded-sm mt-2" required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="inquiry-name" />
          </div>
          <div>
            <Label className="label-mono">Email</Label>
            <Input className="rounded-sm mt-2" type="email" required value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="inquiry-email" />
          </div>
        </div>
        <div>
          <Label className="label-mono">Business name</Label>
          <Input className="rounded-sm mt-2" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="inquiry-business" />
        </div>
        <div>
          <Label className="label-mono">Goals</Label>
          <Textarea className="rounded-sm mt-2 min-h-[110px]" required value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} data-testid="inquiry-goals" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-mono">Budget range</Label>
            <Input className="rounded-sm mt-2" placeholder="e.g. $10–25K" required value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="inquiry-budget" />
          </div>
          <div>
            <Label className="label-mono">Timeline</Label>
            <Input className="rounded-sm mt-2" placeholder="e.g. 6–8 weeks" required value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} data-testid="inquiry-timeline" />
          </div>
        </div>
        <div>
          <Label className="label-mono">Expected deliverables</Label>
          <Textarea className="rounded-sm mt-2 min-h-[100px]" required value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })} data-testid="inquiry-deliverables" />
        </div>
        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={busy} className="rounded-sm bg-[#0047FF] hover:bg-[#6a36b3]" data-testid="inquiry-submit">
            {busy ? "Sending…" : "Send brief"}
          </Button>
        </div>
      </form>
    </div>
  );
}
